import { motion } from 'framer-motion';
import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';

import { ChainOfThoughtProps } from '@/types';
import { useI18n } from '@/locales/client';
import { CDN_BASE_URL } from '@/lib/constants/cdn';

export function ChainOfThought({
  onComplete,
  scrollToBottom,
}: ChainOfThoughtProps) {
  const t = useI18n();
  const [currentIndex, setCurrentIndex] = useState(0);
  const cleanupRef = useRef<(() => void)[]>([]);

  const processingText = t('chainOfThought.processingText');
  const messages = [
    t('chainOfThought.messages.0'),
    t('chainOfThought.messages.1'),
    t('chainOfThought.messages.2'),
    t('chainOfThought.messages.3'),
    t('chainOfThought.messages.4'),
  ];

  const messageDelay = 1500;
  const completionDelay = 2000;

  useEffect(() => {
    if (currentIndex < messages.length) {
      const messageTimer = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        scrollToBottom('smooth');
      }, messageDelay);

      cleanupRef.current.push(() => clearTimeout(messageTimer));
      return () => clearTimeout(messageTimer);
    } else {
      const completeTimer = setTimeout(() => {
        onComplete();
        scrollToBottom('smooth');
      }, completionDelay);

      cleanupRef.current.push(() => clearTimeout(completeTimer));
      return () => clearTimeout(completeTimer);
    }
  }, [currentIndex, messages.length, onComplete, scrollToBottom]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup());
      cleanupRef.current = [];
    };
  }, []);

  return (
    <div className='flex w-full justify-start space-x-4 rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-purple-600'>
        <div className='relative h-[24px] w-[24px]'>
          <Image
            src={`${CDN_BASE_URL}/JAQLNLogoWhite.png`}
            alt='Jaqln Logo'
            fill
            priority
            style={{ objectFit: 'cover' }}
            sizes='24px'
          />
        </div>
      </div>
      <div className='flex-1'>
        <p className='text-md mb-2 font-bold text-gray-500'>{processingText}</p>
        <div className='space-y-2'>
          {messages.slice(0, currentIndex).map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.3 }}
            >
              <p className='text-sm font-medium text-gray-700'>{msg}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
